class InteractiveMap extends HTMLElement {
  constructor() {
    super();
    this.hotspots = [];
    this.titles = [];
    this.activeIndex = -1;
    this.init();
  }

  init() {
    // Get all hotspots and titles
    this.hotspots = Array.from(this.querySelectorAll('.interactive-map__hotspot'));
    this.titles = Array.from(this.querySelectorAll('.interactive-map__content-title'));
    
    // Add event listeners
    this.addEventListeners();
    
    // Set up ARIA attributes for accessibility
    this.setupAccessibility();
  }

  addEventListeners() {
    // Add click listeners to titles
    this.titles.forEach((title, index) => {
      title.addEventListener('mouseenter', () => this.activateItem(index));
      title.addEventListener('click', () => this.activateItem(index));
      title.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.activateItem(index);
        }
      });
    });

    // Add click listeners to hotspots
    this.hotspots.forEach((hotspot, index) => {
        hotspot.addEventListener('mouseenter', () => this.activateItem(index));
      hotspot.addEventListener('click', () => this.activateItem(index));
      hotspot.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.activateItem(index);
        }
      });
    });
  }

  setupAccessibility() {
    // Make titles focusable and add proper ARIA attributes
    this.titles.forEach((title, index) => {
      title.setAttribute('tabindex', '0');
      title.setAttribute('role', 'button');
      title.setAttribute('aria-pressed', 'false');
      title.setAttribute('aria-describedby', `hotspot-${index}`);
    });

    // Add proper ARIA attributes to hotspots
    this.hotspots.forEach((hotspot, index) => {
      hotspot.setAttribute('aria-label', `Hotspot ${index + 1}`);
      hotspot.setAttribute('id', `hotspot-${index}`);
      hotspot.setAttribute('aria-describedby', `title-${index}`);
    });

    // Add IDs to titles for ARIA relationships
    this.titles.forEach((title, index) => {
      title.setAttribute('id', `title-${index}`);
    });

    // Add role and aria-label to the container
    this.setAttribute('role', 'region');
    this.setAttribute('aria-label', 'Interactive Map');
  }

  activateItem(index) {
    // Remove active state from all items
    this.clearActiveStates();
    
    // Set new active index
    this.activeIndex = index;
    
    // Add active state to selected items
    if (this.hotspots[index]) {
      this.hotspots[index].classList.add('active');
      this.hotspots[index].setAttribute('aria-pressed', 'true');
    }
    
    if (this.titles[index]) {
      this.titles[index].classList.add('active');
      this.titles[index].setAttribute('aria-pressed', 'true');
    }

    // Pan to the hotspot
    this.panToHotspot(index);
  }

  panToHotspot(index) {
    const hotspot = this.hotspots[index];
    if (!hotspot) return;

    // Get the panzoom instance
    const panzoomElement = this.querySelector('pan-zoom');
    if (!panzoomElement || !panzoomElement.panzoom) return;

    const panzoom = panzoomElement.panzoom;
    
    // Get the panzoom container
    const panzoomContainer = panzoomElement.querySelector('.panzoom-container');
    if (!panzoomContainer) return;

    // Get the current scale
    const currentScale = panzoom.getScale();
    
    // Calculate the hotspot's position in the original image coordinates
    const hotspotStyle = window.getComputedStyle(hotspot);
    const yPercent = parseFloat(hotspotStyle.getPropertyValue('--y')) / 100;
    
    // Get the image element
    const image = panzoomContainer.querySelector('img');
    if (!image) return;
    
    // Calculate the hotspot's absolute position
    const imageRect = image.getBoundingClientRect();
    const hotspotY = imageRect.height * yPercent;
    
    // Calculate the container center
    const containerRect = panzoomContainer.getBoundingClientRect();
    const containerCenterY = containerRect.height / 2;
    
    // Calculate the pan position needed to center the hotspot
    const panX = ((parseFloat(hotspotStyle.getPropertyValue('--x')) * 10) - (window.innerWidth / 2)) * -1;
    const panY = containerCenterY - (hotspotY * currentScale);
    
    if(window.innerWidth > 991) return;
    
    // Smoothly pan to the new position
    panzoom.pan(panX, panY, {
      animate: true,
      duration: 800,
      easing: 'ease-out'
    });
    panzoom.zoom(1.125, {
      animate: true,
      duration: 800,
      easing: 'ease-out'
    });
  }

  clearActiveStates() {
    // Remove active state from all hotspots
    this.hotspots.forEach((hotspot, index) => {
      hotspot.classList.remove('active');
      hotspot.setAttribute('aria-pressed', 'false');
    });

    // Remove active state from all titles
    this.titles.forEach((title, index) => {
      title.classList.remove('active');
      title.setAttribute('aria-pressed', 'false');
    });
  }
}

// Register the custom element
customElements.define('interactive-map', InteractiveMap);
